import { Button, Card, CardContent } from '@repo/ui';

import { systemRoleLabel } from '@/constants/labels';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { selectActiveRole, selectHeldRoles } from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

import { NoAccessMaintainerNote } from './NoAccessMaintainerNote';
import { SwitchToAccessRoleButton } from './SwitchToAccessRoleButton';

/**
 * Signing in and being allowed to do something are different questions, and
 * only the first is answered by the identity provider. Someone who reaches
 * this screen is authenticated, so sending them back to sign in again would
 * loop — they would return holding exactly the permissions they already have.
 */
export default function NoAccessPage({ email }: { email: string }) {
  const signOut = useSignOut();
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);

  return (
    <div className="flex h-full items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-5 py-10 text-center">
          <div className="flex flex-col gap-2">
            <span className="text-base font-semibold">You do not have access yet</span>
            <span className="text-sm text-muted-foreground">
              {activeRole
                ? `You are signed in as ${email} as ${systemRoleLabel(activeRole)}, which holds no permission this app uses.`
                : `You are signed in as ${email}, but your account holds no role.`}{' '}
              {heldRoles.length > 1
                ? 'Another role you hold may open it.'
                : 'An administrator has to grant a role before there is anything here to show.'}
            </span>
            <NoAccessMaintainerNote />
          </div>

          <SwitchToAccessRoleButton heldRoles={heldRoles} />

          <Button variant="outline" onClick={signOut}>
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
